import React, { useState, useEffect, useRef, useMemo } from 'react'

/* ─────────────────────────────────────────────────────────────
   Private Aviation — Service Request Form

   Airport data: fetched once from mwgg/Airports (GitHub, ~29k entries,
   no API key, no cost). Module-level cache prevents re-fetching between
   form navigations.
───────────────────────────────────────────────────────────── */

const AIRPORTS_URL =
  'https://raw.githubusercontent.com/mwgg/Airports/master/airports.json'

// Module-level cache — survives re-renders and navigation between forms
let airportCache = null

function useAirports() {
  const [airports, setAirports] = useState(airportCache || [])
  const [loading,  setLoading]  = useState(!airportCache)

  useEffect(() => {
    if (airportCache) { setLoading(false); return }

    fetch(AIRPORTS_URL)
      .then(r => r.json())
      .then(data => {
        // data is { ICAO: { iata, name, city, country, … }, … }
        const list = Object.values(data)
          .filter(a => a.iata && a.iata.length === 3 && a.name && a.city)
          .map(a => ({
            code:    a.iata,
            name:    a.name,
            city:    a.city,
            country: a.country || '',
          }))
        airportCache = list
        setAirports(list)
        setLoading(false)
      })
      .catch(() => {
        // Fail silently — user can still type manually
        airportCache = []
        setLoading(false)
      })
  }, [])

  return { airports, loading }
}

/* ── Airport search — filters the cached list locally (no network per keystroke) ── */
function useAirportSearch(airports, query) {
  return useMemo(() => {
    if (!query || query.length < 2) return []
    const q = query.toLowerCase()
    return airports
      .filter(
        a =>
          a.code.toLowerCase().startsWith(q) ||
          a.city.toLowerCase().startsWith(q) ||
          a.name.toLowerCase().includes(q)   ||
          a.city.toLowerCase().includes(q)
      )
      // Prioritise exact IATA prefix matches to the top
      .sort((a, b) => {
        const aCode = a.code.toLowerCase().startsWith(q) ? 0 : 1
        const bCode = b.code.toLowerCase().startsWith(q) ? 0 : 1
        return aCode - bCode
      })
      .slice(0, 8)
  }, [airports, query])
}

/* ── Detect mobile (for camera capture) ── */
function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const check = () =>
      setMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent))
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return mobile
}

/* ── Stepper ── */
function Stepper({ label, value, onChange, min = 0, max = 20 }) {
  return (
    <div className="svc-stepper">
      <span className="svc-stepper__label">{label}</span>
      <div className="svc-stepper__controls">
        <button
          type="button"
          className="svc-stepper__btn"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >−</button>
        <span className="svc-stepper__value">{value}</span>
        <button
          type="button"
          className="svc-stepper__btn"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label={`Increase ${label}`}
        >+</button>
      </div>
    </div>
  )
}

/* ── Airport field ── */
function AirportField({ label, id, value, onChange, airports, loading }) {
  const [open,  setOpen]  = useState(false)
  const [query, setQuery] = useState(value?.display || '')
  const results           = useAirportSearch(airports, query)

  const select = (airport) => {
    setQuery(`${airport.city} (${airport.code})`)
    onChange(airport)
    setOpen(false)
  }

  const showLoading   = open && loading && query.length >= 2
  const showResults   = open && !loading && results.length > 0
  const showNoResults = open && !loading && query.length >= 2 && results.length === 0

  return (
    <div className="svc-field">
      <label className="svc-label" htmlFor={id}>{label}</label>
      <div className="svc-airport-wrap">
        <input
          id={id}
          type="text"
          className="svc-input"
          value={query}
          placeholder="City or IATA code (e.g. BRU)"
          autoComplete="off"
          onChange={e => { setQuery(e.target.value); setOpen(true); onChange(null) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
        />

        {showLoading && (
          <ul className="svc-airport-dropdown">
            <li className="svc-airport-option svc-airport-option--muted" aria-live="polite">
              Loading airports…
            </li>
          </ul>
        )}

        {showResults && (
          <ul className="svc-airport-dropdown">
            {results.map(a => (
              <li key={`${a.code}-${a.name}`}>
                <button
                  type="button"
                  className="svc-airport-option"
                  onMouseDown={() => select(a)}
                >
                  <span className="svc-airport-option__code">{a.code}</span>
                  <span className="svc-airport-option__name">{a.name}</span>
                  <span className="svc-airport-option__city">
                    {a.city}{a.country ? `, ${a.country}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {showNoResults && (
          <ul className="svc-airport-dropdown">
            <li className="svc-airport-option svc-airport-option--muted">
              No airports found for "{query}"
            </li>
          </ul>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   PASSPORT UPLOAD — camera-optimised for mobile
   On mobile  : two buttons — Camera and File
   On desktop : single "Upload document" button
   Accepts    : JPG, PNG, PDF. Max 10 MB.
───────────────────────────────────────────────────────────── */
function PassportUpload({ label, isMobile }) {
  const [file,  setFile]  = useState(null)
  const [error, setError] = useState('')

  const cameraRef = useRef(null)
  const fileRef   = useRef(null)

  const handleFile = (f) => {
    if (!f) return
    if (f.size > 10 * 1024 * 1024) {
      setError('File exceeds 10 MB. Please choose a smaller file.')
      return
    }
    setError('')
    setFile(f)
  }

  return (
    <div className="svc-passport-item">
      <span className="svc-passport-item__label">{label}</span>

      {file ? (
        <div className="svc-passport-item__file">
          <span className="svc-passport-item__check" aria-hidden="true">✓</span>
          <span className="svc-passport-item__name">{file.name}</span>
          <button
            type="button"
            className="svc-passport-item__remove"
            onClick={() => { setFile(null); setError('') }}
            aria-label="Remove file"
          >×</button>
        </div>
      ) : (
        <div className="svc-passport-item__actions">
          {isMobile ? (
            <>
              {/* Camera capture — opens rear camera directly */}
              <label className="svc-upload-btn svc-upload-btn--camera" aria-label="Take photo of document">
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])}
                />
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                  <path d="M1 4.5C1 3.9 1.4 3.5 2 3.5H3L4 2H9L10 3.5H11C11.6 3.5 12 3.9 12 4.5V10C12 10.6 11.6 11 11 11H2C1.4 11 1 10.6 1 10V4.5Z" stroke="currentColor" strokeWidth="0.85" strokeLinejoin="round" fill="none"/>
                  <circle cx="6.5" cy="7" r="1.8" stroke="currentColor" strokeWidth="0.85" fill="none"/>
                </svg>
                Camera
              </label>

              {/* File picker — gallery or files */}
              <label className="svc-upload-btn" aria-label="Choose from files">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])}
                />
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                  <rect x="1.5" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="0.85" fill="none"/>
                  <path d="M4 5.5h5M4 7.5h3" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round"/>
                </svg>
                File
              </label>
            </>
          ) : (
            <label className="svc-upload-btn">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
              Upload document
            </label>
          )}
        </div>
      )}

      {error && <span className="svc-passport-item__error">{error}</span>}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   PASSENGER ROW — supports companion selection
───────────────────────────────────────────────────────────── */
function PassengerPassportRow({ index, label, savedCompanions, isMobile }) {
  const [companionId, setCompanionId] = useState('self')

  const hasCompanions = savedCompanions && savedCompanions.length > 0
  const isOwn         = companionId === 'self'
  const companion     = savedCompanions?.find((_, i) => String(i) === companionId)

  return (
    <div className="svc-passenger-row">
      <div className="svc-passenger-row__header">
        <span className="svc-passenger-row__label">{label}</span>

        {hasCompanions && (
          <select
            className="svc-companion-select"
            value={companionId}
            onChange={e => setCompanionId(e.target.value)}
            aria-label={`Select saved companion for ${label}`}
          >
            <option value="self">
              {index === 0 ? 'Myself' : 'Select companion…'}
            </option>
            {savedCompanions.map((c, i) => (
              <option key={i} value={String(i)}>
                {c.name} ({c.role})
              </option>
            ))}
            <option value="other">Other passenger</option>
          </select>
        )}
      </div>

      {/* Pre-filled companion data */}
      {!isOwn && companionId !== 'other' && companion && (
        <div className="svc-companion-prefill">
          <div className="svc-companion-prefill__row">
            <span className="svc-companion-prefill__key">Name</span>
            <span className="svc-companion-prefill__val">{companion.name}</span>
          </div>
          <div className="svc-companion-prefill__row">
            <span className="svc-companion-prefill__key">Passport</span>
            <span className="svc-companion-prefill__val">{companion.passport || '—'}</span>
          </div>
          <div className="svc-companion-prefill__row">
            <span className="svc-companion-prefill__key">Nationality</span>
            <span className="svc-companion-prefill__val">{companion.nationality || '—'}</span>
          </div>
          <p className="svc-companion-prefill__note">
            Your document on file. Upload a new one if details have changed.
          </p>
        </div>
      )}

      <PassportUpload label="Passport document" isMobile={isMobile} />
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function PrivateAviation({ onBack, member, savedCompanions = [] }) {
  const isMobile = useIsMobile()

  // Airport data — fetched once, cached at module level
  const { airports, loading: airportsLoading } = useAirports()

  const [submitted,      setSubmitted]      = useState(false)
  const [showConfirm,    setShowConfirm]    = useState(false)
  const [tripType,       setTripType]       = useState('one-way')
  const [origin,         setOrigin]         = useState(null)
  const [destination,    setDestination]    = useState(null)
  const [departDate,     setDepartDate]     = useState('')
  const [returnDate,     setReturnDate]     = useState('')
  const [flexDates,      setFlexDates]      = useState(false)
  const [cabinClass,     setCabinClass]     = useState('business')
  const [adults,         setAdults]         = useState(1)
  const [children,       setChildren]       = useState(0)
  const [childAges,      setChildAges]      = useState([])
  const [infants,        setInfants]        = useState(0)
  const [bags,           setBags]           = useState(0)
  const [bagWeight,      setBagWeight]      = useState('')
  const [includeAirport, setIncludeAirport] = useState(false)
  const [specialReqs,    setSpecialReqs]    = useState('')

  const updateChildren = (n) => {
    setChildren(n)
    setChildAges(prev => {
      const next = [...prev]
      while (next.length < n) next.push('')
      return next.slice(0, n)
    })
  }

  const updateChildAge = (i, val) =>
    setChildAges(prev => prev.map((a, idx) => idx === i ? val : a))

  const isUrgent = () => {
    if (!departDate) return false
    return (new Date(departDate) - new Date()) < 24 * 60 * 60 * 1000
  }

  const handleSubmit  = (e) => { e.preventDefault(); setShowConfirm(true) }
  const handleConfirm = ()  => { setShowConfirm(false); setSubmitted(true) }

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">Request Received</h2>
          <p className="svc-submitted__body">
            Your request has been received. We will be in touch within the hour.
          </p>
          <p className="svc-submitted__sig">— Mwuïlu</p>
          <button className="svc-btn-ghost" onClick={onBack}>Return to Dashboard</button>
        </div>
      </div>
    )
  }

  /* ── Confirmation modal ── */
  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">Please review your request</p>
          <h2 className="svc-confirm__heading">Private Aviation</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label="Trip type"  value={tripType === 'return' ? 'Return' : 'One Way'} />
            <ConfirmRow label="From"       value={origin      ? `${origin.city} (${origin.code})`           : '—'} />
            <ConfirmRow label="To"         value={destination ? `${destination.city} (${destination.code})` : '—'} />
            <ConfirmRow label="Departure"  value={departDate  || '—'} />
            {tripType === 'return' && (
              <ConfirmRow label="Return" value={returnDate || '—'} />
            )}
            <ConfirmRow label="Flexible"   value={flexDates ? '±2 days' : 'No'} />
            <ConfirmRow label="Cabin"      value={cabinClass === 'first' ? 'First Class' : 'Business Class'} />
            <ConfirmRow
              label="Passengers"
              value={`${adults} adult${adults !== 1 ? 's' : ''}${children ? `, ${children} child${children !== 1 ? 'ren' : ''}` : ''}${infants ? `, ${infants} infant${infants !== 1 ? 's' : ''}` : ''}`}
            />
            {bags > 0 && (
              <ConfirmRow label="Checked bags" value={`${bags}${bagWeight ? ` × ${bagWeight} kg` : ''}`} />
            )}
            {includeAirport && (
              <ConfirmRow label="Airport services" value="Included" />
            )}
            <ConfirmRow label="Passport documents" value="✓ Uploaded" />
            {specialReqs && (
              <ConfirmRow label="Special requirements" value={specialReqs} />
            )}
          </div>
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>Edit</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>Confirm &amp; Submit</button>
          </div>
        </div>
      </div>
    )
  }

  /* ── Form ── */
  return (
    <div className="db-page svc-page">

      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label="Back">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">Service Request</p>
          <h1 className="svc-header__title">Private Aviation</h1>
        </div>
        {isUrgent() && (
          <div className="svc-urgent">
            For departures within 24 hours, we are available to assist you directly.{' '}
            <a href="tel:+32471xxxxxx">Contact us directly. Any hour, without exception.</a>
          </div>
        )}
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Trip type ── */}
        <div className="svc-section">
          <p className="svc-section__label">Trip Type</p>
          <div className="svc-toggle-group">
            <button
              type="button"
              className={`svc-toggle${tripType === 'one-way' ? ' svc-toggle--active' : ''}`}
              onClick={() => setTripType('one-way')}
            >One Way</button>
            <button
              type="button"
              className={`svc-toggle${tripType === 'return' ? ' svc-toggle--active' : ''}`}
              onClick={() => setTripType('return')}
            >Return</button>
          </div>
        </div>

        {/* ── Route ── */}
        <div className="svc-section">
          <p className="svc-section__label">Route</p>
          <div className="svc-row-2">
            <AirportField
              label="Origin"
              id="origin"
              value={origin}
              onChange={setOrigin}
              airports={airports}
              loading={airportsLoading}
            />
            <AirportField
              label="Destination"
              id="destination"
              value={destination}
              onChange={setDestination}
              airports={airports}
              loading={airportsLoading}
            />
          </div>
        </div>

        {/* ── Dates ── */}
        <div className="svc-section">
          <p className="svc-section__label">Dates</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="departDate">Departure Date</label>
              <input
                id="departDate"
                type="date"
                className="svc-input"
                value={departDate}
                onChange={e => setDepartDate(e.target.value)}
                required
              />
            </div>
            {tripType === 'return' && (
              <div className="svc-field">
                <label className="svc-label" htmlFor="returnDate">Return Date</label>
                <input
                  id="returnDate"
                  type="date"
                  className="svc-input"
                  value={returnDate}
                  onChange={e => setReturnDate(e.target.value)}
                />
              </div>
            )}
          </div>
          <label className="svc-checkbox-label">
            <input
              type="checkbox"
              className="svc-checkbox"
              checked={flexDates}
              onChange={e => setFlexDates(e.target.checked)}
            />
            <span>Flexible dates (±2 days)</span>
          </label>
        </div>

        {/* ── Cabin class ── */}
        <div className="svc-section">
          <p className="svc-section__label">Cabin Class</p>
          <div className="svc-toggle-group">
            <button
              type="button"
              className={`svc-toggle${cabinClass === 'business' ? ' svc-toggle--active' : ''}`}
              onClick={() => setCabinClass('business')}
            >Business Class</button>
            <button
              type="button"
              className={`svc-toggle${cabinClass === 'first' ? ' svc-toggle--active' : ''}`}
              onClick={() => setCabinClass('first')}
            >First Class</button>
          </div>
        </div>

        {/* ── Passengers ── */}
        <div className="svc-section">
          <p className="svc-section__label">Passengers</p>
          <div className="svc-steppers">
            <Stepper label="Adults"   value={adults}   onChange={setAdults}       min={1} />
            <Stepper label="Children" value={children} onChange={updateChildren}          />
            <Stepper label="Infants"  value={infants}  onChange={setInfants}              />
          </div>
          {children > 0 && (
            <div className="svc-child-ages">
              {childAges.map((age, i) => (
                <div key={i} className="svc-field svc-field--inline">
                  <label className="svc-label" htmlFor={`childAge${i}`}>Child {i + 1} age</label>
                  <input
                    id={`childAge${i}`}
                    type="number"
                    min="1"
                    max="17"
                    className="svc-input svc-input--sm"
                    value={age}
                    onChange={e => updateChildAge(i, e.target.value)}
                    placeholder="Age"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Luggage ── */}
        <div className="svc-section">
          <p className="svc-section__label">Checked Luggage</p>
          <div className="svc-row-2 svc-row-2--align-end">
            <Stepper label="Number of bags" value={bags} onChange={setBags} />
            {bags > 0 && (
              <div className="svc-field">
                <label className="svc-label" htmlFor="bagWeight">Weight per bag (kg)</label>
                <input
                  id="bagWeight"
                  type="number"
                  min="1"
                  max="50"
                  className="svc-input svc-input--sm"
                  value={bagWeight}
                  onChange={e => setBagWeight(e.target.value)}
                  placeholder="kg"
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Passenger documents ── */}
        <div className="svc-section">
          <p className="svc-section__label">Passenger Documents</p>
          {savedCompanions.length > 0 && (
            <p className="svc-passport-notice" style={{ marginBottom: '16px' }}>
              Select a saved companion to auto-fill their details, or upload a new document.
            </p>
          )}
          <div className="svc-passport-list">
            {Array.from({ length: adults + children }).map((_, i) => (
              <PassengerPassportRow
                key={i}
                index={i}
                label={`Passenger ${i + 1}`}
                savedCompanions={i === 0 ? [] : savedCompanions}
                isMobile={isMobile}
              />
            ))}
          </div>
          <p className="svc-passport-notice">
            Your documents are transmitted securely and handled with complete discretion.
          </p>
        </div>

        {/* ── Add-ons ── */}
        <div className="svc-section">
          <p className="svc-section__label">Additional Services</p>
          <label className="svc-checkbox-label">
            <input
              type="checkbox"
              className="svc-checkbox"
              checked={includeAirport}
              onChange={e => setIncludeAirport(e.target.checked)}
            />
            <span>Include meet &amp; greet and airport transfer for this journey</span>
          </label>
        </div>

        {/* ── Special requirements ── */}
        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="specialReqs">Special Requirements</label>
            <textarea
              id="specialReqs"
              className="svc-textarea"
              rows={3}
              value={specialReqs}
              onChange={e => setSpecialReqs(e.target.value)}
              placeholder="Dietary preferences, accessibility needs, specific requests…"
            />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">
            Review Request
          </button>
        </div>

      </form>
    </div>
  )
}

/* ── Confirm row ── */
function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}